import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DeviceStatus,
  UserDeviceResponse,
} from '@/api/user-portal-api-generated';
import { DevicesService } from '@/api/user-portal-service';
import { queryKeys } from '@/common/queryClient';
import { useToast } from '@/hooks/use-toast';

interface UpdateDeviceParams {
  deviceId: string;
  status: DeviceStatus;
}

export function useInvalidateDevices() {
  const queryClient = useQueryClient();

  return {
    invalidateDevices: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.devices.all() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.enrolledFactors.all(),
        }),
      ]),
  };
}

export function useDeleteDeviceMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, string>({
    mutationFn: async (deviceId: string) => {
      await DevicesService.deleteUserDevice(deviceId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.devices.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.enrolledFactors.all(),
      });

      toast.success({
        title: 'Device Removed',
        message: 'Device removed successfully',
      });
    },
    onError: (error: Error) => {
      toast.error({
        title: 'Unable to Remove Device',
        message: error.message || 'Unable to remove device. Please try again.',
      });
    },
  });
}

export function useUpdateDeviceMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<UserDeviceResponse, Error, UpdateDeviceParams>({
    mutationFn: async ({ deviceId, status }: UpdateDeviceParams) => {
      return DevicesService.updateUserDevice(deviceId, { status });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.devices.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.enrolledFactors.all(),
      });

      toast.success({
        title: 'Device Updated',
        message: 'Device status updated successfully',
      });
    },
    onError: (error: Error) => {
      toast.error({
        title: 'Unable to Update Device',
        message: error.message || 'Unable to update device. Please try again.',
      });
    },
  });
}
